import React, { useRef, useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  Select,
  MenuItem,
  Grid,
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

import CommonModal from "../CommonModal";
import CalendarRangePicker from "./CalendarRangePicker";
import { calculateWorkingDays } from "./dateUtils"; // Import the utility function
import holidays from "./holidays";
import { getFieldStyle, scrollToField } from "../fieldHelpers";

const HolidayRequestForm = ({
  formData,
  handleChange,
  fileNames,
  setFileNames,
  setFileObjects,
  highlightedField,
  fileObjects,
}) => {
  const [openPublicHoliday, setOpenPublicHoliday] = useState(false);
  const [openCompanyClosures, setOpenCompanyClosures] = useState(false);
  const [openPlannedEvents, setOpenPlannedEvents] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState("UK"); // Default to UK
  const [workingDays, setWorkingDays] = useState(0);

  const fieldRefs = {
    leaveType: useRef(null),
    dateRange: useRef(null),
    attachedFile: useRef(null),
    additionalNotes: useRef(null),
    status: useRef(null),
  };

  useEffect(() => {
    scrollToField(highlightedField, fieldRefs); // Use the external helper
  }, [highlightedField]);

  const handleCountryChange = (event) => {
    setSelectedCountry(event.target.value);
  };

  const uploadFile = (e) => {
    const file = e.target?.files[0];
    if (file) {
      setFileNames((prev) => [...prev, file.name]);
      setFileObjects((prev) => ({
        ...prev,
        [file.name]: file, // Map file name to the actual file object
      }));
      handleChange(e);
    }
  };

  const previewFile = (fileName) => {
    const file = fileObjects[fileName];
    if (file) {
      const fileUrl = URL.createObjectURL(file);
      window.open(fileUrl, "_blank"); // Open the file in a new tab
    } else {
      console.error("File not found for preview");
    }
  };

  const removeFile = (fileNameToRemove) => {
    setFileNames((prev) =>
      prev.filter((fileName) => fileName !== fileNameToRemove),
    );
    setFileObjects((prev) => {
      const updatedFiles = { ...prev };
      delete updatedFiles[fileNameToRemove];
      return updatedFiles;
    });
    handleChange({ target: { name: "removeFile", value: fileNameToRemove } });
  };

  useEffect(() => {
    const [start, end] = formData.dateRange || [];
    if (start && end) {
      const startDate = new Date(start);
      const endDate = new Date(end);
      setWorkingDays(calculateWorkingDays(startDate, endDate, holidays.UK));
    }
  }, [formData.dateRange]);

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="body2" sx={{ color: "#888", marginTop: "10px" }}>
            Leave Type
          </Typography>

          <FormControl
            ref={fieldRefs["leaveType"]}
            sx={getFieldStyle("leaveType", highlightedField)}
            fullWidth
            margin="normal"
            required
          >
            <Select
              labelId="leave-type-label"
              name="leaveType"
              value={formData.leaveType || ""}
              onChange={handleChange}
            >
              <MenuItem value="Annual Leave">Annual Leave</MenuItem>
              <MenuItem value="Sick Leave">Sick Leave</MenuItem>
              <MenuItem value="Personal Leave">Personal Leave</MenuItem>
              <MenuItem value="Maternity Leave">Maternity Leave</MenuItem>
              <MenuItem value="Paternity Leave">Paternity Leave</MenuItem>
              <MenuItem value="Compassionate Leave">
                Compassionate Leave
              </MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </Select>
          </FormControl>

          {/* Upload Button and File List */}
          <Button
            variant="outlined"
            component="label"
            sx={{ marginTop: "20px", display: "flex", alignItems: "center" }}
          >
            <AttachFileIcon sx={{ marginRight: "8px" }} />
            Add File
            <input
              type="file"
              hidden
              name="attachedFile"
              onChange={(e) => {
                uploadFile(e);
              }}
            />
          </Button>

          <FormControl
            ref={fieldRefs["attachedFile"]}
            sx={getFieldStyle("attachedFile", highlightedField)}
            fullWidth
            margin="normal"
            required
          >
            <Box sx={{ marginTop: "10px" }}>
              {fileNames.map((fileName, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "5px 10px",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    marginTop: "5px",
                  }}
                >
                  <Typography variant="body2">{fileName}</Typography>
                  <Box>
                    <Button
                      color="primary"
                      size="small"
                      sx={{ marginRight: "8px" }}
                      onClick={() => previewFile(fileName)}
                    >
                      Preview
                    </Button>
                    <Button
                      color="error"
                      size="small"
                      onClick={() => removeFile(fileName)}
                    >
                      Remove
                    </Button>
                  </Box>
                </Box>
              ))}
            </Box>
          </FormControl>
        </Grid>
      </Grid>
      <FormControl
        ref={fieldRefs["dateRange"]}
        fullWidth
        margin="normal"
        sx={getFieldStyle("dateRange", highlightedField)}
        required
      >
        <Grid spacing={2}>
          <CalendarRangePicker
            formatType="holiday"
            formData={formData}
            handleChange={handleChange}
          />
        </Grid>
      </FormControl>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          mt: 1,
        }}
      >
        <AccessTimeIcon sx={{ marginRight: 1, color: "#1976d2" }} />
        <Typography
          variant="body1"
          sx={{
            textAlign: "center",
            fontSize: "large",
          }}
        >
          {workingDays} {workingDays === 1 ? "day" : "days"}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: "20px",
          gap: 1,
        }}
      >
        <Button
          variant="contained"
          sx={{ fontSize: "12px", lineHeight: "18px" }}
          color="secondary"
          onClick={() => setOpenPublicHoliday(true)}
        >
          Public Holidays
        </Button>
        <Button
          variant="contained"
          sx={{ fontSize: "12px", lineHeight: "18px" }}
          color="secondary"
          onClick={() => setOpenCompanyClosures(true)}
        >
          Company Closures
        </Button>
        <Button
          variant="contained"
          sx={{ fontSize: "12px", lineHeight: "18px" }}
          color="secondary"
          onClick={() => setOpenPlannedEvents(true)}
        >
          Planned Events
        </Button>
      </Box>
      <FormControl
        ref={fieldRefs["additionalNotes"]}
        fullWidth
        margin="normal"
        required
      >
        <TextField
          fullWidth
          margin="normal"
          label="Additional Notes"
          multiline
          rows={4}
          name="additionalNotes"
          sx={getFieldStyle("additionalNotes", highlightedField)}
          value={formData.additionalNotes || ""}
          onChange={handleChange}
        />
      </FormControl>
      <CommonModal
        title="Public Holidays"
        open={openPublicHoliday}
        handleClose={() => setOpenPublicHoliday(false)}
        content={
          <div style={{ maxWidth: "100%", overflow: "hidden" }}>
            {/* Country Selection */}
            <label
              htmlFor="country-select"
              style={{
                fontWeight: "bold",
                marginBottom: "0.5rem",
                color: "#333",
              }}
            >
              Select Country:
            </label>
            <select
              id="country-select"
              value={selectedCountry}
              onChange={handleCountryChange}
              style={{
                padding: "0.5rem",
                fontSize: "1rem",
                marginBottom: "1rem",
                display: "block",
                width: "100%",
                maxWidth: "200px",
                border: "1px solid #ccc",
                borderRadius: "4px",
              }}
            >
              <option value="UK">United Kingdom</option>
              <option value="UAE">United Arab Emirates</option>
              <option value="US">United States</option>
            </select>
            <h3
              style={{
                borderBottom: "2px solid #0077b6",
                paddingBottom: "0.5rem",
                marginBottom: "1rem",
                color: "#0077b6",
              }}
            >
              {selectedCountry} Public Holidays - 2025
            </h3>
            {/* Scrollable Holiday List */}
            <div
              style={{
                maxHeight: "300px",
                overflowY: "auto",
                border: "1px solid #e0e0e0",
                borderRadius: "8px",
                padding: "1rem",
                backgroundColor: "#f9f9f9",
              }}
            >
              <ul style={{ listStyleType: "none", paddingLeft: 0 }}>
                {holidays[selectedCountry].map((holiday) => (
                  <li
                    key={holiday.name}
                    style={{
                      marginBottom: "0.75rem",
                      padding: "0.5rem",
                      borderBottom: "1px solid #e0e0e0",
                      color: "#333",
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box
                      component="span"
                      sx={{ fontWeight: "bold", color: "#333" }}
                    >
                      {holiday.name}:
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        color: "#555",
                        display: { xs: "none", sm: "block" },
                      }}
                    >
                      {holiday.date}
                    </Box>
                  </li>
                ))}
              </ul>
            </div>

            {/* Close Button */}
            <Button
              variant="contained"
              color="primary"
              onClick={() => setOpenPublicHoliday(false)}
              style={{ marginTop: "1.5rem", width: "100%" }}
            >
              Close
            </Button>
          </div>
        }
      />
      <CommonModal
        title={"Company Closures"}
        content={"This is content of Company Closures"}
        open={openCompanyClosures}
        handleClose={() => setOpenCompanyClosures(false)}
      />
      <CommonModal
        title={"Planned Events"}
        content={"This is content of Planned Events"}
        open={openPlannedEvents}
        handleClose={() => setOpenPlannedEvents(false)}
      />
    </>
  );
};

export default HolidayRequestForm;
